import z from "zod";
import { baseEntitySchema } from "./base";

export enum PergolaDesign {
  FREE_STANDING = "free_standing",
  ATTACHED = "attached",
}

export enum TextureCode {
  RAL_7015 = "7015.png",
  RAL_7035 = "7035.png",
  RAL_9016 = "9016.png",
  BRONZE = "bronze.png",
}

const LEGACY_CAMEL_SAND_TEXTURE = "camel-sand.png";

/** Maps persisted Firestore values before schema parse. */
export const migrateLegacyPrestigeTexturesFromFirestore = (
  raw: unknown,
): unknown => {
  if (!raw || typeof raw !== "object") return raw;
  const root = raw as Record<string, unknown>;
  const design = root.design;
  if (!design || typeof design !== "object") return raw;
  const d = design as Record<string, unknown>;
  let changed = false;
  const nextDesign = { ...d };
  if (nextDesign.structureColor === LEGACY_CAMEL_SAND_TEXTURE) {
    nextDesign.structureColor = TextureCode.BRONZE;
    changed = true;
  }
  if (nextDesign.louverColor === LEGACY_CAMEL_SAND_TEXTURE) {
    nextDesign.louverColor = TextureCode.BRONZE;
    changed = true;
  }
  if (!changed) return raw;
  return { ...root, design: nextDesign };
};

export enum AccessoryFaceID {
  EAST = "East",
  WEST = "West",
  SOUTH0 = "South0",
  SOUTH1 = "South1",
  NORTH0 = "North0",
  NORTH1 = "North1",
}

export const PlacedAccessorySchema = z.object({
  faceId: z.enum(AccessoryFaceID),
  partType: z.number(),
  movement: z.number().optional(),
  secondaryMovement: z.number().optional(),
  isLeft: z.boolean().optional(),
  texture: z.string().optional(),
  texture2: z.string().optional(),
  mergeDoors: z.array(z.boolean()).max(6).optional(),
});

export type PlacedAccessory = z.infer<typeof PlacedAccessorySchema>;

export const PrestigeConfigurationDataSchema = z.object({
  ownerID: z.string(),
  collaborative: z.boolean().default(true),
  design: z.object({
    pergola: z.enum(PergolaDesign),
    structureColor: z.enum(TextureCode),
    louverColor: z.enum(TextureCode),
  }),
  dimensions: z.object({
    height: z.object({
      feet: z.number(),
      inches: z.number(),
    }),
    length: z.object({
      feet: z.number(),
      inches: z.number(),
    }),
    width: z.object({
      feet: z.number(),
      inches: z.number(),
    }),
  }),
  accessories: z.object({
    ledLight: z.boolean(),
    heaterQuantity: z.number().default(0),
    placed: z.array(PlacedAccessorySchema),
  }),
  buildNumber: z.string(),
});

export type PrestigeConfigurationData = z.infer<
  typeof PrestigeConfigurationDataSchema
>;
export const prestigeConfigurationSchema = baseEntitySchema.extend(
  PrestigeConfigurationDataSchema.shape,
);
export type PrestigeConfiguration = z.infer<typeof prestigeConfigurationSchema>;
